import {
    AfterViewInit,
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    Input,
    OnInit,
    Renderer2,
    ViewChild,
    isDevMode,
    signal,
} from '@angular/core';

import { WindowRef } from '@frontend/vanilla/core';
import fastdom from 'fastdom';

export interface ITextTruncate {
    toggleTextTruncate: boolean;
    maxLineCount: number;
    truncationText: string;
}

/* TextTruncateComponent
 * ------------------------
 * This component applies an ellipsis to overflowing text.
 *
 * Performance Warning:
 * - Custom ellipsis(class="apply-truncation") requires measuring `scrollHeight`, which may trigger reflows.
 * - If using custom ellipsis extensively, consider performance optimizations.
 */

@Component({
    selector: 'ms-text-truncate',
    template: `<div #container>
        <ng-content></ng-content>
        @if (applyCustomTruncation()) {
            <div class="truncate-element">
                ...<span class="more-text">{{ truncationSettings.truncationText }}</span>
            </div>
        }
    </div>`,
    styleUrl: './text-truncate.component.scss',
    standalone: true,
})
export class TextTruncateComponent implements OnInit, AfterViewInit {
    @Input() truncationSettings: ITextTruncate;
    @Input() showDefaultEllipsis = true;
    @ViewChild('container') container: ElementRef;
    applyCustomTruncation = signal(false);
    private isTruncated = signal(false);
    private applyEllipsis = signal(false);
    private scrollHeight: number;
    private maxHeight: number;

    @HostBinding('class') className = 'content-truncation';
    @HostBinding('class.apply-ellipsis') get ellipsisClass(): boolean {
        return this.isTruncated();
    }

    @HostBinding('class.apply-truncation') get truncationClass(): boolean {
        return this.applyCustomTruncation();
    }

    @HostListener('click')
    onContainerClick(): void {
        if (this.applyEllipsis()) {
            this.isTruncated.update((prev) => !prev);
        }

        if (this.applyCustomTruncation()) {
            this.toggleExpansion();
        }
    }

    constructor(
        private renderer: Renderer2,
        private window: WindowRef,
    ) {}

    ngOnInit(): void {
        this.applyEllipsis.set(this.showDefaultEllipsis && !this.truncationSettings.toggleTextTruncate);
        this.isTruncated.set(this.applyEllipsis());
    }

    ngAfterViewInit(): void {
        if (!this.applyEllipsis()) {
            this.applyTextTruncation();
        }
    }

    private applyTextTruncation(): void {
        if (!this.scrollHeight && !this.maxHeight) {
            if (isDevMode()) {
                console.log('Please review the usage of "<ms-text-truncate />" might have perf issues');
            }
            fastdom.measure(() => {
                const lineHeight = this.window.nativeWindow.getComputedStyle(this.container.nativeElement).lineHeight;
                this.scrollHeight = this.container.nativeElement.scrollHeight;
                this.maxHeight = parseFloat(lineHeight) * this.truncationSettings.maxLineCount;
            });
        }

        fastdom.mutate(() => {
            if (this.scrollHeight > this.maxHeight) {
                this.renderer.setStyle(this.container.nativeElement, 'max-height', `${this.maxHeight}px`);
                this.applyCustomTruncation.update((prev) => !prev);
            }
        });
    }

    private toggleExpansion(): void {
        if (this.applyCustomTruncation()) {
            this.applyCustomTruncation.update((prev) => !prev);
            this.renderer.setStyle(this.container.nativeElement, 'max-height', 'none');
        }
    }
}
